import { useEffect, useMemo, useState } from 'react'
import { trackProjectVizOpened } from 'Services/Telemetry/index.js'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeftIcon, ChevronDownIcon, ChevronRightIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { SortableTableHeader } from './SortableTableHeader.js'
import type { Column, Filter, FilterType } from './OtuDataTableView.logic.js'
import {
  blastTypeFallback,
  DEFAULT_SORT_FIELD,
  effectiveGroupMode,
  effectiveSort,
  emptyTableText,
  filterChipText,
  splitFiltersByMode,
  spinViewUnavailable,
  formatAsOf,
  groupModeOptions,
  groupModeUnavailableReason,
  hasFilter,
  nextSort,
  parseFilterValuesFromUrl,
  parseFiltersFromUrl,
  initialHiddenColumns,
  parseReadsViewFromUrl,
  spinModeDisabledReason,
  truncationNote,
  withFilterApplied,
  withFilterCleared,
  otuTableColumns,
  autoHiddenColumns,
  isColumnShown,
  withColumnToggled,
  writeReadsViewToUrl
} from './OtuDataTableView.logic.js'
import { useOtuCubeTable } from './useOtuCubeTable.js'
import type { OtuTableSource } from './OtuTableSource.js'
import type { GroupSourceChoice } from './GroupSource.js'
import { isReadsColumnField, readsColumnField, relativeAbundance, type OtuTableRow } from './OtuCubeTable.logic.js'
import type { OtuSearchResult } from '../../../Generated/OtuSearchResult.ts'
import { firstValueFrom } from 'rxjs'
import { BlastSource } from '../../../Model/Project/BlastSource.js'
import { GroupModes, type GroupModeKey } from '../../../Generated/GroupMode.ts'
import React from 'react'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.ts'
import { errorMessage, infoMessage } from '../../../Model/Message/IMessage.ts'
import { useSelector } from '../../../Flux'
import type { State } from '../../../Flux/Slice'
import { useGetAuthenticatedUser } from '../../../Flux/Selector'
import { useUserWasFetched } from '../../../Flux/Selector/Authentication/GetAuthenticatedUser/UserWasFetchedSelector.js'
import { canChooseSpinAlgorithm, canUseSpin, canUseSpinOnOrder } from '../../../Model'
import { resolveSpinAlgorithm, visibleSpinAlgorithms } from '../../../Model/Spin/spinAlgorithm.js'
import { describeSpinStatus } from '../../../Model/Spin/spinStatus.js'
import { useSpinScopeStatus } from '../../Component/SpinStatusLabel.js'
import { getOrder } from '../../../Repository/Order/OrderRepository.js'
import type { IOrder } from '../../../Model/index.js'
import { useSpinAlgorithms, useSpinChangePoints } from '../../Component/useSpinMeta.ts'
import { SampleReadsMenu, type ReadsColumn, type ReadsGrouping } from './SampleReadsMenu.js'
import { EXPORT_CELL_WARNING, csvCellCount, cubeCsv, cubeCsvFields } from './OtuCubeExport.js'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import { OtuDetailsDrawer } from './OtuDetailsDrawer.js'
import { RowDrawer } from './RowDrawer.js'
import { useOtuDetails } from './useOtuDetails.js'
import { OtuResultInfoStrip } from './OtuResultInfoStrip.js'
import { REDUCTION_PRESETS, isPresetActive, presetLabel, withPresetToggled, type ReductionPreset } from './OtuReductionPresets.js'
import type { ResultSettings } from './OtuResultInfo.logic.js'
import { ScopeOwnerGate } from './ScopeOwnerGate.js'
// Re-exported so SortableTableHeader / AutocompleteFilterInput keep importing these from here.
export type { Column, FilterType } from './OtuDataTableView.logic.js'

type OtuDataTableViewProps = {
  type: 'PROJECT' | 'ORDER'
  /**
   * Embedded mode (e.g. the chat canvas). When set, the scope is FORCED via `scopeId` (not the route `:id`),
   * initial state is seeded from `initialParams` (not the live URL), and URL-sync + full-page chrome (back
   * button, header, fixed loading overlay) are suppressed so the table can mount standalone. Existing
   * full-page usage passes none of these and is unaffected.
   */
  embedded?: boolean
  scopeId?: string
  initialParams?: URLSearchParams
}

const READS_NOT_LOADED = 'Reads per sample could not be loaded, so they cannot be exported.'

const NO_SAMPLES: string[] = []
const NO_ALGORITHMS: string[] = []

function TableSkeleton() {
  return (
    <div data-testid="otu-table-skeleton" className="flex min-h-96 flex-col gap-2" aria-busy="true">
      <div className="skeleton h-10 w-full" />
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="skeleton h-6 w-full" />
      ))}
    </div>
  )
}

function OtuTableLoading() {
  return (
    <div className="bg-slate-100 rounded-lg p-4 drop-shadow-sm m-auto w-full">
      <div className="overflow-y-auto min-h-96" style={{ minWidth: 900 }}>
        <div data-testid="otu-controls-skeleton" className="skeleton mb-4 h-8 w-full" />
        <TableSkeleton />
      </div>
    </div>
  )
}

function saveFile(blob: Blob, name: string) {
  const objectUrl = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(objectUrl)
}

export function OtuDataTableView(props: OtuDataTableViewProps) {
  const { id: routeId } = useParams<{ id: string }>()
  return (
    // The chat canvas embeds scopes the backend already resolved for the user, so it has no owner to ask.
    <ScopeOwnerGate type={props.type} id={props.scopeId ?? routeId} enabled={!props.embedded} loading={<OtuTableLoading />}>
      {(order) => <OtuDataTable {...props} ownerOrder={order} />}
    </ScopeOwnerGate>
  )
}

function OtuDataTable({
  type,
  embedded = false,
  scopeId,
  initialParams,
  ownerOrder
}: OtuDataTableViewProps & { ownerOrder: IOrder | null }) {
  const { id: routeId } = useParams<{ id: string }>()
  const id = scopeId ?? routeId
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [liveSearchParams] = useSearchParams()
  // Seed initial state from the forced params (embedded) or the live URL (full-page). Consumed only by the
  // useState initialisers below (read once on mount), so this is a stable seed, not a reactive source.
  const searchParams = initialParams ?? liveSearchParams

  // Initialize state from URL parameters or defaults
  const [selectedBlastType, setSelectedBlastType] = useState<BlastSource>(
    (searchParams.get('blastType') as BlastSource) || BlastSource.BOLD
  )
  // What the user picked; the table groups by `groupMode`, which differs while the blast type cannot group by it.
  const [selectedGroupMode, setSelectedGroupMode] = useState<GroupModeKey>(
    (searchParams.get('groupMode') as GroupModeKey) || 'PROCESS_ID'
  )

  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '0'))
  const [explicitSortField, setSortField] = useState<string | null>(searchParams.get('sortField'))
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>((searchParams.get('sortDirection') as 'ASC' | 'DESC') || 'DESC')
  const [filters, setFilters] = useState<Filter[]>(parseFiltersFromUrl(searchParams))
  const [filterDialogOpen, setFilterDialogOpen] = useState<string | null>(null)
  const [filterValues, setFilterValues] = useState<Record<string, string>>(parseFilterValuesFromUrl(searchParams))
  const [hiddenColumns, setHiddenColumns] = useState<Record<string, boolean>>(initialHiddenColumns(searchParams))
  const [columnVisibilityMenuOpen, setColumnVisibilityMenuOpen] = useState<boolean>(false)
  const pageSize = 20

  const [pageLoaded, setPageLoaded] = useState<boolean>(false)

  const [isExporting, setIsExporting] = useState(false)

  // --- SPIN mode: one row per current SPIN (resolved live from spin.*), user picks algorithm + point-in-time. ---
  const user = useGetAuthenticatedUser()
  // Which controls and table mode the user gets depends on their licence, so nothing renders or loads before it is known.
  const userFetched = useUserWasFetched()
  const orderState = useSelector((s: typeof State) => (type === 'ORDER' ? s.order?.order : undefined))
  const reduxOrder = orderState && orderState !== 'LOADING' && orderState.id === id ? orderState : null
  const knownOrder = reduxOrder ?? (ownerOrder?.id === id ? ownerOrder : null)
  const [fetchedOrder, setFetchedOrder] = useState<IOrder | null>(null)
  const order = knownOrder ?? (fetchedOrder?.id === id ? fetchedOrder : null)
  // SPIN follows INSIGHT on the scope: a lab-shared licence counts on an order, a project needs the user's own.
  const spinAccess = type === 'ORDER' ? canUseSpinOnOrder(order, user) : canUseSpin(user)
  const algorithmChoice = canChooseSpinAlgorithm(user)

  const scopeAlgorithms = useSpinAlgorithms(id, spinAccess)
  // A failed fetch reads as "no SPIN data", which disables the mode with its reason.
  const scopeSpinAlgorithms = scopeAlgorithms.error ? NO_ALGORITHMS : scopeAlgorithms.algorithms
  const spinAlgorithms = useMemo(
    () => (scopeSpinAlgorithms === null ? null : visibleSpinAlgorithms(scopeSpinAlgorithms, algorithmChoice)),
    [scopeSpinAlgorithms, algorithmChoice]
  )
  const [viewMode, setViewMode] = useState<'CLASSIC' | 'SPIN'>((searchParams.get('viewMode') as 'CLASSIC' | 'SPIN') || 'CLASSIC')
  const [requestedSpinAlgorithm, setSpinAlgorithm] = useState<string>(searchParams.get('spinAlgorithm') || '')
  const spinAlgorithm = resolveSpinAlgorithm(requestedSpinAlgorithm, spinAlgorithms, algorithmChoice)
  const [spinAtTime, setSpinAtTime] = useState<string>(searchParams.get('spinAtTime') || '') // '' = latest
  const [initialReadsView] = useState(() => parseReadsViewFromUrl<ReadsGrouping>(searchParams))
  const [showReads, setShowReads] = useState<boolean>(initialReadsView.showReads)
  const [readsGrouping, setReadsGrouping] = useState<ReadsGrouping | null>(initialReadsView.readsGrouping)
  const [relativeReads, setRelativeReads] = useState<boolean>(initialReadsView.relativeReads)
  const [readsSamples, setReadsSamples] = useState<string[] | null>(initialReadsView.readsSamples ?? null)
  const [sampleReadsColumns, setSampleReadsColumns] = useState<ReadsColumn[]>([])

  // Entitled users always see the switch; `spinDisabledReason` is non-null when it can't be used yet.
  const spinStatus = useSpinScopeStatus(id, spinAccess)
  const spinDisabledReason = spinModeDisabledReason(spinAlgorithms, type, spinStatus)
  // A partly clustered project can use SPIN already; the tooltip still says which orders are missing.
  const spinStatusDescription = describeSpinStatus(spinStatus, type)
  const viewModeTooltip = spinDisabledReason ?? (spinStatusDescription.kind === 'pending' ? spinStatusDescription.text : null)
  const isSpin = viewMode === 'SPIN'
  const spinChangePoints = useSpinChangePoints(id, spinAlgorithm, isSpin)

  const groupModeFallback = effectiveGroupMode(selectedGroupMode, selectedBlastType)
  const groupMode = groupModeFallback.groupMode
  const groupModeMessage = groupModeFallback.message
  useEffect(() => {
    if (groupModeMessage === null || isSpin) {
      return
    }
    dispatch(addMessage(infoMessage(groupModeMessage)))
  }, [groupModeMessage, isSpin, dispatch])

  const allColumns = useMemo(() => otuTableColumns(viewMode, selectedBlastType), [viewMode, selectedBlastType])
  // Reads-per-sample-group columns appended inline (scroll right). One column per selected bucket.
  const readsColumns: Column[] = useMemo(
    () =>
      (showReads ? sampleReadsColumns : []).map((c) => ({
        field: readsColumnField(c.key),
        label: c.label,
        headerLabel: c.headerLabel,
        isNumber: true,
        filterable: false,
        sortable: true,
        readsSampleIds: c.sampleIds,
        verticalHeader: true
      })),
    [showReads, sampleReadsColumns]
  )
  const readsColumnIndex = useMemo(() => new Map(readsColumns.map((c, i) => [c.field, i])), [readsColumns])
  const fullColumns = useMemo(() => [...allColumns, ...readsColumns], [allColumns, readsColumns])
  const { sortField, sortDirection: appliedSortDirection } = effectiveSort(fullColumns, explicitSortField ?? DEFAULT_SORT_FIELD, sortDirection)

  const sampleColumns = useMemo(() => (showReads ? sampleReadsColumns : null), [showReads, sampleReadsColumns])
  const modeFilters = useMemo(() => splitFiltersByMode(filters, viewMode, selectedBlastType), [filters, viewMode, selectedBlastType])
  const tableMode = useMemo((): { source: GroupSourceChoice | null; settings: ResultSettings; exportLabel: string } => {
    if (!isSpin) {
      return {
        source: { kind: 'classic', groupMode },
        settings: { grouping: GroupModes[groupMode]?.label ?? groupMode },
        exportLabel: groupMode
      }
    }
    const changePoint = spinChangePoints.find((cp) => cp.assignedAt === spinAtTime)
    return {
      // SPIN rows follow the licence, which is only known once the user is.
      source: userFetched ? { kind: 'spin', algorithm: spinAlgorithm, atTime: spinAtTime } : null,
      settings: { spin: { algorithm: spinAlgorithm, asOf: spinAtTime === '' ? 'Latest' : changePoint ? formatAsOf(changePoint) : spinAtTime } },
      exportLabel: `SPIN_${spinAlgorithm}`
    }
  }, [isSpin, userFetched, groupMode, spinAlgorithm, spinAtTime, spinChangePoints])
  const table: OtuTableSource = useOtuCubeTable({
    scopeId: id,
    blastType: selectedBlastType,
    source: tableMode.source,
    filters: modeFilters.active,
    sortField,
    sortDirection: appliedSortDirection,
    page: currentPage,
    pageSize,
    sampleColumns,
    representationVisible: isColumnShown('representation', hiddenColumns, new Set())
  })
  const { results: otuResults, isLoading } = table
  const sampleTotals = table.sampleReads.totals

  // Row drawers: rows that group OTUs expand to their OTUs' details, cached per OTU while the scope stays.
  const membersOf = table.membersOf
  const drawers = !!membersOf
  const resultInfo = !embedded
  const otuDetails = useOtuDetails(drawers ? id : undefined)
  const [expandedRows, setExpandedRows] = useState<ReadonlySet<string>>(new Set())
  useEffect(() => setExpandedRows(new Set()), [id, selectedBlastType, groupMode, viewMode, currentPage, filters])
  const requestMembers = table.requestMembers
  useEffect(() => {
    if (requestMembers && expandedRows.size > 0) requestMembers([...expandedRows])
  }, [requestMembers, expandedRows])
  const toggleRow = (rowId: string) =>
    setExpandedRows((rows) => {
      const next = new Set(rows)
      if (next.has(rowId)) next.delete(rowId)
      else next.add(rowId)
      return next
    })
  useEffect(() => {
    const fallback = blastTypeFallback(selectedBlastType, table.sources)
    if (!fallback) return
    dispatch(addMessage(infoMessage(fallback.message)))
    setSelectedBlastType(fallback.blastType)
    setCurrentPage(0)
  }, [selectedBlastType, table.sources, dispatch])
  const blastTypeOptions = useMemo(() => {
    const sources = table.sources
    if (!sources) return Object.values(BlastSource)
    return Object.values(BlastSource).filter((s) => sources.includes(s) || s === selectedBlastType)
  }, [table.sources, selectedBlastType])

  // Project-scoped OTU table opens, counted on arrival. The order variant is counted at the context
  // menu that opens it (see `trackVizOpened`); projects have no such menu, so without this the
  // route recorded nothing at all.
  useEffect(() => {
    if (type === 'PROJECT' && id) trackProjectVizOpened('otu_table', id)
  }, [type, id])

  // The shared-licence half of the SPIN gate lives on the order. The chat canvas skips the owner gate, so it fetches
  // here: the global order slice belongs to the order page, and the canvas may embed several order tables.
  const needsOrder = type === 'ORDER' && !!id && !knownOrder
  useEffect(() => {
    if (!needsOrder || !id) return
    let cancelled = false
    firstValueFrom(getOrder(id))
      .then((o) => !cancelled && setFetchedOrder(o))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [needsOrder, id])

  // An order's shared licence is only known with the order; a user's own licence decides without it.
  const spinAccessKnown = userFetched && (spinAccess || type !== 'ORDER' || order !== null)
  const spinUnavailable = spinViewUnavailable({ accessKnown: spinAccessKnown, spinAccess, algorithms: spinAlgorithms })
  useEffect(() => {
    if (viewMode !== 'SPIN' || !spinUnavailable) {
      return
    }
    setViewMode('CLASSIC')
    setCurrentPage(0)
  }, [viewMode, spinUnavailable])

  const changeViewMode = (mode: 'CLASSIC' | 'SPIN') => {
    setViewMode(mode)
    setCurrentPage(0)
  }

  async function exportCsv() {
    if (!id) return
    const visibleReadsColumns = readsColumns.filter((c) => isColumnVisible(c.field))
    const fields = allColumns.filter((c) => isColumnVisible(c.field)).map((c) => c.field)
    const name = `otu_export_${type.toLowerCase()}_${id}_${selectedBlastType}_${tableMode.exportLabel}.csv`
    const rowCount = typeof otuResults === 'object' ? otuResults.totalSize : 0
    const cells = csvCellCount(rowCount, cubeCsvFields(new Set(fields)).length + visibleReadsColumns.length)
    if (
      cells > EXPORT_CELL_WARNING &&
      !window.confirm(`This export has ${cells.toLocaleString()} cells (${rowCount.toLocaleString()} rows) and may take a while to build. Export anyway?`)
    ) {
      return
    }
    try {
      setIsExporting(true)
      let exported: Awaited<ReturnType<typeof table.exportRows>>
      try {
        exported = await table.exportRows()
      } catch (e) {
        const timedOut = typeof e === 'object' && e !== null && 'status' in e && e.status === 408
        const message = timedOut
          ? 'Maximum query time exceeded, please filter your data'
          : visibleReadsColumns.length > 0
            ? READS_NOT_LOADED
            : `Could not load the rows to export: ${e}`
        dispatch(addMessage(errorMessage(message)))
        return
      }
      if (!exported) {
        dispatch(addMessage(errorMessage('The table is not ready to export.')))
        return
      }
      const { rows, sampleTotals: exportTotals } = exported
      // Let the spinner paint before the synchronous build.
      await new Promise((resolve) => setTimeout(resolve, 0))
      const readsIndices = visibleReadsColumns.map((c) => readsColumnIndex.get(c.field)!)
      const csv = cubeCsv({
        rows,
        columns: new Set(fields),
        reads:
          visibleReadsColumns.length > 0
            ? {
                labels: visibleReadsColumns.map((c) => (relativeReads ? `${c.label} (%)` : c.label)),
                indices: readsIndices,
                totals: readsIndices.map((i) => exportTotals![i]!),
                relative: relativeReads
              }
            : null,
        comment: table.truncation ? truncationNote(table.truncation) : null
      })
      saveFile(new Blob([csv], { type: 'text/csv;charset=utf-8' }), name)
    } catch (e) {
      dispatch(addMessage(errorMessage(`Could not build the csv: ${e}`)))
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    if (!pageLoaded) {
      setPageLoaded(true)
    }
  }, [])

  useEffect(() => {
    if (!pageLoaded) return
    // Embedded (chat canvas): never write to the URL — it isn't this table's URL to own.
    if (embedded) return

    const params = new URLSearchParams()

    params.set('blastType', selectedBlastType)
    params.set('page', currentPage.toString())
    if (explicitSortField) {
      params.set('sortField', explicitSortField)
      params.set('sortDirection', sortDirection)
    }
    params.set('groupMode', selectedGroupMode)
    params.set('viewMode', viewMode)
    if (isSpin) {
      if (spinAlgorithm) params.set('spinAlgorithm', spinAlgorithm)
      if (spinAtTime) params.set('spinAtTime', spinAtTime)
    }
    writeReadsViewToUrl(params, { showReads, readsGrouping, relativeReads, readsSamples })

    if (filters.length > 0) {
      params.set('filters', encodeURIComponent(JSON.stringify(filters)))
    }

    if (Object.keys(filterValues).length > 0) {
      params.set('filterValues', encodeURIComponent(JSON.stringify(filterValues)))
    }

    if (Object.keys(hiddenColumns).length > 0) {
      params.set('hiddenColumns', encodeURIComponent(JSON.stringify(hiddenColumns)))
    }

    navigate({ search: `?${params.toString()}` }, { replace: true })
  }, [
    filters,
    explicitSortField,
    sortDirection,
    selectedBlastType,
    selectedGroupMode,
    currentPage,
    filterValues,
    hiddenColumns,
    viewMode,
    isSpin,
    spinAlgorithm,
    spinAtTime,
    showReads,
    readsGrouping,
    relativeReads,
    readsSamples
  ])

  const content = useMemo(() => (otuResults && typeof otuResults !== 'string' ? otuResults.content : []), [otuResults])

  const filteredRows = table.filteredRows
  const autoHidden = useMemo(() => autoHiddenColumns(allColumns, filteredRows ?? []), [allColumns, filteredRows])
  const toggleColumnVisibility = (field: string) => setHiddenColumns((prev) => withColumnToggled(field, prev, autoHidden))
  const isColumnVisible = (field: string) => isColumnShown(field, hiddenColumns, autoHidden)

  const handleBlastTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBlastType(e.target.value as BlastSource)
    setCurrentPage(0) // Reset to first page when changing blast type
  }

  const handleGroupModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGroupMode(e.target.value as GroupModeKey)
    setCurrentPage(0) // Reset to first page when changing group mode
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  const handleBack = () => {
    if (type == 'PROJECT') {
      navigate(`/projects/${id}`)
    } else {
      navigate(`/order/${id}`)
    }
  }

  const handleSort = (field: string) => {
    const next = nextSort({ sortField, sortDirection: appliedSortDirection }, field)
    setSortField(next.sortField)
    setSortDirection(next.sortDirection)
    setCurrentPage(0) // Reset to first page when changing sort
  }

  const handleFilterClick = (field: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    setFilterDialogOpen(filterDialogOpen === field ? null : field)
  }

  const applyFilter = (field: string, value: string, filterType: FilterType = 'LIKE') => {
    if (!value) {
      clearFilter(field)
      return
    }

    setFilterValues({ ...filterValues, [field]: value })
    setFilters(withFilterApplied(filters, field, value, filterType))
    setFilterDialogOpen(null)
    setCurrentPage(0) // Reset to first page when applying filter
  }

  const clearFilter = (field: string) => {
    setFilters(withFilterCleared(filters, field))
    setFilterValues({ ...filterValues, [field]: '' })
    setFilterDialogOpen(null)
    setCurrentPage(0) // Reset to first page when clearing filter
  }

  const isFiltered = (field: string) => hasFilter(filters, field)

  const togglePreset = (preset: ReductionPreset) => {
    const next = withPresetToggled(filters, preset)
    setFilters(next)
    setFilterValues({ ...filterValues, [preset.field]: hasFilter(next, preset.field) ? preset.value : '' })
    setCurrentPage(0)
  }

  const toggleReads = () => {
    if (showReads && isReadsColumnField(sortField)) setSortField(null)
    setShowReads((v) => !v)
  }

  // Rows carry their reads pivoted, in `readsColumns` order; a row without them is still loading, or failed.
  const readsCell = (field: string, row: OtuTableRow) => {
    const i = readsColumnIndex.get(field)!
    const v = row.sampleReads?.[i]
    if (v === undefined) {
      return <td key={field} className="text-right">{table.sampleReads.status === 'error' ? '-' : '…'}</td>
    }
    const relative = relativeReads ? relativeAbundance(v, sampleTotals?.[i] ?? 0) : v
    return (
      <td key={field} className={'text-right ' + (v === 0 ? 'text-gray-300' : '')}>
        {v === 0 ? '·' : Number.isNaN(relative) ? '-' : relativeReads ? relative.toFixed(2) : v.toLocaleString()}
      </td>
    )
  }
  const visibleBaseColumns = allColumns.filter((c) => isColumnVisible(c.field)).length

  // Disabled rather than hidden when the scope has no SPIN coverage, so "not clustered yet" stops
  // looking like "not entitled". Only wrapped in a tooltip while it has something to say — TooltipTrigger
  // pushes provider state on every mousemove, which is wasted work on a control that works fine.
  const viewModeSelect = (
    <select
      data-testid="otu-viewmode-select"
      className="select select-bordered select-sm"
      value={viewMode}
      disabled={spinDisabledReason !== null}
      onChange={(e) => changeViewMode(e.target.value as 'CLASSIC' | 'SPIN')}
    >
      <option value="CLASSIC">Classic (OTU)</option>
      <option value="SPIN">SPIN</option>
    </select>
  )

  if (!userFetched) {
    return <OtuTableLoading />
  }

  return (
    <div className="bg-slate-100 rounded-lg p-4 drop-shadow-sm m-auto w-full">
      {isLoading && !embedded && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="flex flex-col items-center gap-2">
            <span className="loading loading-spinner loading-lg text-primary"></span>
            <p className="text-white text-sm">Loading...</p>
          </div>
        </div>
      )}
      <div className="overflow-y-auto min-h-96" style={{ minWidth: 900 }}>
        <div className="flex items-center mb-4">
          {/* Full-page chrome only: in the chat canvas the panel has its own title and there's nowhere to go "back" to. */}
          {!embedded && (
            <button className="btn btn-sm btn-ghost mr-4" onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              Back to {type == 'PROJECT' ? 'Project' : 'Order'}
            </button>
          )}
          <h1 className="text-2xl grow whitespace-nowrap">{embedded ? '' : 'Analysis Data'}</h1>
          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
            <div className="relative">
              <button
                data-testid="otu-columns-toggle"
                className="btn btn-sm btn-outline"
                onClick={() => setColumnVisibilityMenuOpen(!columnVisibilityMenuOpen)}
              >
                {columnVisibilityMenuOpen ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                <span className="ml-1">Columns</span>
              </button>
              {columnVisibilityMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white shadow-lg rounded-lg z-50 p-2">
                  <div className="font-bold mb-2 pb-1 border-b">Toggle Column Visibility</div>
                  {fullColumns.map((column) => (
                    <div key={column.field} className="flex items-center p-1 hover:bg-gray-100">
                      <input
                        type="checkbox"
                        data-testid={`otu-colvis-${column.field}`}
                        className="checkbox checkbox-sm mr-2"
                        checked={isColumnVisible(column.field)}
                        onChange={() => toggleColumnVisibility(column.field)}
                      />
                      <span>{column.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center">
              <span className="mr-2 whitespace-nowrap">Blast Type:</span>
              <select
                data-testid="otu-blast-select"
                className="select select-bordered select-sm"
                value={selectedBlastType}
                onChange={handleBlastTypeChange}
              >
                {blastTypeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            {spinAccess && (
              <div className="flex items-center">
                <span className="mr-2 whitespace-nowrap">View:</span>
                {/* The hover lands on the wrapper, not the disabled select — that is what makes the
                    tooltip reachable at all, so the wrapper must stay outside the control. */}
                {viewModeTooltip === null ? (
                  viewModeSelect
                ) : (
                  <TooltipTrigger tooltip={viewModeTooltip}>{viewModeSelect}</TooltipTrigger>
                )}
              </div>
            )}
            {!isSpin ? (
              <div className="flex items-center">
                <span className="mr-2 whitespace-nowrap">Grouping Type:</span>
                <select
                  data-testid="otu-groupmode-select"
                  className="select select-bordered select-sm"
                  title={groupModeMessage ?? undefined}
                  value={groupMode}
                  onChange={handleGroupModeChange}
                >
                  {groupModeOptions().map((mode) => {
                    const unavailable = groupModeUnavailableReason(mode.key, selectedBlastType)
                    return (
                      <option key={mode.key} value={mode.key} disabled={unavailable !== null} title={unavailable ?? undefined}>
                        {unavailable === null ? mode.label : `${mode.label} (not for ${selectedBlastType})`}
                      </option>
                    )
                  })}
                </select>
              </div>
            ) : (
              <>
                {(spinAlgorithms?.length ?? 0) > 1 && (
                  <div className="flex items-center">
                    <span className="mr-2 whitespace-nowrap">Algorithm:</span>
                    <select
                      data-testid="otu-spin-algorithm"
                      className="select select-bordered select-sm"
                      value={spinAlgorithm}
                      onChange={(e) => {
                        setSpinAlgorithm(e.target.value)
                        setSpinAtTime('')
                        setCurrentPage(0)
                      }}
                    >
                      {(spinAlgorithms ?? []).map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex items-center">
                  <span className="mr-2 whitespace-nowrap">As of:</span>
                  <select
                    className="select select-bordered select-sm"
                    value={spinAtTime}
                    onChange={(e) => {
                      setSpinAtTime(e.target.value)
                      setCurrentPage(0)
                    }}
                  >
                    <option value="">Latest</option>
                    {spinChangePoints.map((cp) => (
                      <option key={cp.assignedAt} value={cp.assignedAt}>
                        {formatAsOf(cp)}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <SampleReadsMenu
              key={viewMode}
              type={type}
              scopeId={id ?? ''}
              sampleIds={table.sampleReads.sampleIds ?? NO_SAMPLES}
              status={table.sampleReads.status === 'error' ? 'error' : table.sampleReads.sampleIds ? 'ready' : 'loading'}
              onColumnsChange={setSampleReadsColumns}
              initialGrouping={readsGrouping}
              onGroupingChange={setReadsGrouping}
              initialPicks={readsSamples}
              onPicksChange={setReadsSamples}
              enabled={showReads}
              onToggle={toggleReads}
              relative={relativeReads}
              onRelativeChange={setRelativeReads}
            />
            <div className={'flex items-center'}>
              <button
                data-testid="otu-export"
                className="btn btn-sm btn-outline"
                onClick={exportCsv}
                disabled={isExporting || isLoading || !otuResults || typeof otuResults === 'string'}
                title="Export current view (all pages) as CSV"
              >
                {isExporting ? (
                  <>
                    <span className="loading loading-spinner loading-xs mr-2"></span>
                    Exporting…
                  </>
                ) : (
                  'Export CSV'
                )}
              </button>
            </div>
          </div>
        </div>

        {resultInfo && <OtuResultInfoStrip scopeId={id} settings={tableMode.settings} note={table.resultNote} />}

        <div className="my-2 flex flex-wrap items-center gap-x-1">
          Active Filters:{' '}
          {filters.length <= 0 ? (
            <span className="italic">None</span>
          ) : (
            <>
              {modeFilters.active.map((filter, index) => (
                <span key={index} data-testid={`otu-filter-chip-${filter.parameter}`} className="badge bold p-4 m-1 badge-primary">
                  {filterChipText(filter, fullColumns)}
                  <span className="ml-4 btn btn-xs btn-circle cursor-pointer text-error" onClick={() => clearFilter(filter.parameter)}>
                    X
                  </span>
                </span>
              ))}
              {modeFilters.inactive.map(({ filter, text, onlyIn }) => (
                <span
                  key={filter.parameter}
                  data-testid={`otu-filter-chip-${filter.parameter}`}
                  data-inactive="true"
                  title={`Not applied: this column exists in the ${onlyIn} view only.`}
                  className="badge p-4 m-1 badge-ghost text-gray-500"
                >
                  <span className="line-through">{text}</span>
                  <span className="ml-2 text-xs">({onlyIn} only)</span>
                  <span className="ml-4 btn btn-xs btn-circle cursor-pointer text-error" onClick={() => clearFilter(filter.parameter)}>
                    X
                  </span>
                </span>
              ))}
            </>
          )}
          <span className="ml-2 flex flex-wrap items-center gap-1 text-sm">
            <span className="text-gray-500">Add:</span>
            {REDUCTION_PRESETS.map((preset) => {
              const active = isPresetActive(filters, preset)
              return (
                <button
                  key={`${preset.field}:${preset.value}`}
                  data-testid="otu-filter-preset"
                  className={'btn btn-xs ' + (active ? 'btn-primary' : 'btn-outline')}
                  aria-pressed={active}
                  onClick={() => togglePreset(preset)}
                >
                  {presetLabel(preset, fullColumns)}
                </button>
              )
            })}
        </span>
        </div>

        {table.truncation && (
          <div data-testid="otu-cube-truncated" className="alert alert-info my-2 py-2 text-sm">
            <span>{truncationNote(table.truncation)}</span>
            <button data-testid="otu-cube-load-all" className="btn btn-sm btn-outline" onClick={table.loadAll}>
              Load all
            </button>
          </div>
        )}

        {!otuResults ? (
          <TableSkeleton />
        ) : otuResults == 'ERROR' ? (
          <div className="alert alert-error">
            <span>Error loading analysis data</span>
            <button data-testid="otu-cube-retry" className="btn btn-sm btn-outline" onClick={table.retry}>
              Retry
            </button>
          </div>
        ) : otuResults.content.length === 0 ? (
          <div className="alert alert-info">
            <span>{emptyTableText(!isSpin && table.groupedOut, groupMode, selectedBlastType)}</span>
          </div>
        ) : (
          <>
            <div className="overflow-auto max-h-[calc(100vh-15rem)] min-h-96">
              <table className="table table-zebra table-pin-rows whitespace-nowrap">
                <thead>
                  <tr>
                    {drawers && <th className="w-6" aria-label="Details"></th>}
                    {fullColumns.map(
                      (column, index) =>
                        isColumnVisible(column.field) && (
                          <SortableTableHeader
                            key={index}
                            column={column}
                            sortField={sortField}
                            sortDirection={appliedSortDirection}
                            isFiltered={isFiltered}
                            handleSort={handleSort}
                            handleFilterClick={handleFilterClick}
                            filterDialogOpen={filterDialogOpen}
                            currentValue={filterValues[column.field]}
                            applyFilter={applyFilter}
                            clearFilter={clearFilter}
                            id={id || ''}
                            type={type}
                            selectedBlastType={selectedBlastType}
                          />
                        )
                    )}
                  </tr>
                  {readsColumns.some((c) => isColumnVisible(c.field)) && (
                    <tr data-testid="otu-sample-totals">
                      {visibleBaseColumns + (drawers ? 1 : 0) > 0 && (
                        <th colSpan={visibleBaseColumns + (drawers ? 1 : 0)} className="text-right">
                          {relativeReads ? 'Sample total = 100 %' : 'Total reads per sample'}
                        </th>
                      )}
                      {readsColumns.map((column, i) => {
                        if (!isColumnVisible(column.field)) return null
                        const total = sampleTotals?.[i]
                        return (
                          <th key={column.field} className="text-right">
                            {total === undefined ? '…' : relativeReads ? (total > 0 ? '100.00' : '-') : total.toLocaleString()}
                          </th>
                        )
                      })}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {content.map((otu) => (
                    <React.Fragment key={otu.otuId}>
                      <tr data-testid="otu-table-row">
                        {drawers && (
                          <td className="w-6">
                            <button
                              data-testid="otu-row-expand"
                              className="btn btn-ghost btn-xs px-1"
                              aria-label={expandedRows.has(otu.otuId) ? 'Hide details' : 'Show details'}
                              aria-expanded={expandedRows.has(otu.otuId)}
                              onClick={() => toggleRow(otu.otuId)}
                            >
                              {expandedRows.has(otu.otuId) ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                            </button>
                          </td>
                        )}
                        {fullColumns.map((column) => {
                          if (!isColumnVisible(column.field)) return null

                          if (column.readsSampleIds) return readsCell(column.field, otu)

                          if (column.field === 'representation' && otu.representationPending) {
                            return <td key={column.field}>{table.representationLoading ? '…' : '-'}</td>
                          }

                          const field = column.field as keyof OtuSearchResult
                          let value = otu[field]
                          if (column.transformer) {
                            return <td key={column.field}>{column.transformer(value, otu)}</td>
                          } else {
                            if (column.formatter) {
                              value = column.formatter(value)
                            }
                            return <td key={column.field}>{value || '-'}</td>
                          }
                        })}
                      </tr>
                      {membersOf && id && expandedRows.has(otu.otuId) && (
                        <tr data-testid="otu-row-drawer">
                          <td colSpan={fullColumns.filter((c) => isColumnVisible(c.field)).length + 1} className="bg-white">
                            <div className="sticky left-0 max-w-[calc(100vw-6rem)]">
                              <RowDrawer
                                members={membersOf(otu.otuId)}
                                render={(memberIds) => (
                                  <OtuDetailsDrawer scopeId={id} memberIds={memberIds} details={otuDetails} scopeSources={table.sources} />
                                )}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-center mt-4">
              <div className="join">
                <button
                  data-testid="otu-page-prev"
                  className="join-item btn"
                  disabled={currentPage === 0}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  «
                </button>
                <button data-testid="otu-page-indicator" className="join-item btn">
                  Page {currentPage + 1} of {Math.ceil(otuResults.totalSize / pageSize)}
                </button>
                <button
                  data-testid="otu-page-next"
                  className="join-item btn"
                  disabled={currentPage >= Math.ceil(otuResults.totalSize / pageSize) - 1}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  »
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
